import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  useTheme,
  alpha,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  Chip,
  Divider
} from "@mui/material";
import { Link } from "react-router-dom";
import CoinCard from "../components/CoinCard";
import ApplicationForm from "../components/ApplicationForm";
import { motion } from "framer-motion";
import HomeIcon from '@mui/icons-material/Home';
import PublicIcon from '@mui/icons-material/Public';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import HistoryIcon from '@mui/icons-material/History';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

// US coins data - All authentic American coins with your images
// Premium/Diamond coins marked with isPremium flag
const usaCoins = [
  // ========== PREMIUM & DIAMOND COINS (DISPLAYED FIRST) ==========
  {
    id: 401,
    name: "Faberge Diamond Trellis Egg Silver Proof Coin",
    year: "2024",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$12,500 – $18,500",
    priceNum: 15500,
    image: "/assets/images/usacoins/2024 $1 Faberge Diamond Trellis Egg Silver Proof Coin  Direct Coins.png",
    description: "Exquisite Faberge-inspired silver proof coin featuring intricate diamond trellis design. Limited edition with genuine diamond accents. A true masterpiece of numismatic art.",
    metal: "Silver with Diamonds",
    category: "usa",
    mint: "Direct Coins",
    era: "Modern",
    isPremium: true,
    isDiamond: true
  },
  {
    id: 402,
    name: "14k Gold Ring with 1908 Indian Chief Head Coin",
    year: "1908",
    grade: "AU58",
    gradeNum: 58,
    price: "$8,500 – $12,500",
    priceNum: 10500,
    image: "/assets/images/usacoins/14k Gold ring with a 22K 2.5 Dollar 1908 Indian Chief Head Coin bezel.png",
    description: "Stunning wearable numismatic piece featuring a genuine 1908 Indian Chief Head $2.5 Gold Coin set in a luxurious 14k gold ring. The perfect fusion of jewelry and coin collecting.",
    metal: "22K Gold Coin in 14K Setting",
    category: "usa",
    mint: "Custom",
    era: "Early 20th Century",
    isPremium: true,
    isDiamond: false,
    isWearable: true
  },
  {
    id: 403,
    name: "Bald Eagle 2004 Liberia Silver Coin with Gold",
    year: "2004",
    grade: "Proof 69",
    gradeNum: 69,
    price: "$2,800 – $4,200",
    priceNum: 3500,
    image: "/assets/images/usacoins/Bald Eagle (USA) - 2004 - Liberia - 10 Dollars - silver coin with gold.png",
    description: "Magnificent dual-metal commemorative featuring the American Bald Eagle. Silver coin with applied gold accents from the Republic of Liberia. Limited mintage.",
    metal: "Silver with Gold",
    category: "usa",
    mint: "Liberia Mint",
    era: "Modern",
    isPremium: true,
    isDiamond: false
  },

  // ========== GOLD RUSH ERA COINS ==========
  {
    id: 306,
    name: "Augustus Humbert $50 Gold Slug",
    year: "1851",
    grade: "AU53",
    gradeNum: 53,
    price: "$85,000",
    priceNum: 85000,
    image: "/assets/images/usacoins/The Legendary Humbert $50 Gold PieceWhy Authentication Determines Six .png",
    description: "California Gold Rush era $50 gold 'Slug' from the U.S. Assay Office. Extremely rare territorial gold with historical significance.",
    metal: "Gold",
    category: "usa",
    mint: "US Assay Office",
    era: "Gold Rush Era"
  },
  {
    id: 307,
    name: "1849 Gold Half Eagle",
    year: "1849",
    grade: "MS61",
    gradeNum: 61,
    price: "$24,500",
    priceNum: 24500,
    image: "/assets/images/usacoins/1849 Gold Half Eagle Greg & Norris MS-61 NGC.png",
    description: "First year of issue for the Liberty Head Half Eagle during the height of the California Gold Rush. NGC certified.",
    metal: "Gold",
    category: "usa",
    mint: "US Mint",
    era: "Gold Rush Era"
  },

  // ========== CLASSIC GOLD COINS ==========
  {
    id: 301,
    name: "Saint-Gaudens Double Eagle",
    year: "1924",
    grade: "MS64",
    gradeNum: 64,
    price: "$32,000",
    priceNum: 32000,
    image: "/assets/images/usacoins/1924 $20 Saint Gauden's Gold Double Eagle.png",
    description: "Classic Saint-Gaudens design featuring Lady Liberty with a torch and powerful eagle in flight. One of America's most beautiful coins.",
    metal: "Gold",
    category: "usa",
    mint: "US Mint",
    era: "Early 20th Century"
  },
  {
    id: 302,
    name: "Coronet Liberty Head Double Eagle",
    year: "1881",
    grade: "MS62",
    gradeNum: 62,
    price: "$42,000",
    priceNum: 42000,
    image: "/assets/images/usacoins/UNITED STATES, San Francisco mint, gold Coronet Liberty Head double.png",
    description: "Classic Liberty Head Coronet design from the San Francisco mint. Exceptional example from the late 19th century.",
    metal: "Gold",
    category: "usa",
    mint: "San Francisco Mint",
    era: "Late 19th Century"
  },
  {
    id: 303,
    name: "Indian Head $10 Gold Eagle",
    year: "1907",
    grade: "MS62",
    gradeNum: 62,
    price: "$48,000",
    priceNum: 48000,
    image: "/assets/images/usacoins/1907 $10 Gold Indian Head Eagle PCGS MS-62 Indian .png",
    description: "Stunning Indian Head design with concave surfaces and intricate feather details. PCGS certified MS-62.",
    metal: "Gold",
    category: "usa",
    mint: "US Mint",
    era: "Early 20th Century"
  },
  {
    id: 308,
    name: "Indian Head $5 Gold Half Eagle",
    year: "1915",
    grade: "MS65",
    gradeNum: 65,
    price: "$28,500",
    priceNum: 28500,
    image: "/assets/images/usacoins/1915 $5.00 Indian Head Gold Half Eagle .png",
    description: "Stunning Indian Head Half Eagle with sharp strike and original luster. Beautiful incuse design.",
    metal: "Gold",
    category: "usa",
    mint: "US Mint",
    era: "Early 20th Century"
  },
  {
    id: 310,
    name: "Liberty Head $10 Gold Eagle",
    year: "1893",
    grade: "MS63",
    gradeNum: 63,
    price: "$35,000",
    priceNum: 35000,
    image: "/assets/images/usacoins/1893 Liberty Head $10 Gold Eagle Values.png",
    description: "Classic Liberty Head design from 1893. Exceptional eye appeal and original surfaces.",
    metal: "Gold",
    category: "usa",
    mint: "US Mint",
    era: "Late 19th Century"
  },

  // ========== RARE SILVER COINS ==========
  {
    id: 311,
    name: "1893-CC Morgan Silver Dollar",
    year: "1893",
    grade: "AU55",
    gradeNum: 55,
    price: "$85,000",
    priceNum: 85000,
    image: "/assets/images/usacoins/1893-CC Morgan Silver Dollar.png",
    description: "Extremely rare Carson City Morgan Dollar with very low mintage. Highly sought after by collectors.",
    metal: "Silver",
    category: "usa",
    mint: "Carson City Mint",
    era: "Late 19th Century"
  },
  {
    id: 312,
    name: "Peace Silver Dollar",
    year: "1921",
    grade: "MS64",
    gradeNum: 64,
    price: "$15,000",
    priceNum: 15000,
    image: "/assets/images/usacoins/1921 Silver Peace Dollar Coins.png",
    description: "First year of issue for the Peace Dollar, commemorating the end of World War I. High relief design.",
    metal: "Silver",
    category: "usa",
    mint: "US Mint",
    era: "Early 20th Century"
  },
  {
    id: 313,
    name: "Walking Liberty Half Dollar",
    year: "1943",
    grade: "MS67",
    gradeNum: 67,
    price: "$8,500",
    priceNum: 8500,
    image: "/assets/images/usacoins/Silver 1943 d Walking Liberty Half Dollar Coin.png",
    description: "Beautiful Walking Liberty design in superb gem condition with full bands. Iconic silver coin.",
    metal: "Silver",
    category: "usa",
    mint: "US Mint",
    era: "Mid 20th Century"
  },
  {
    id: 314,
    name: "Oregon Trail Memorial Half Dollar",
    year: "1936",
    grade: "MS66",
    gradeNum: 66,
    price: "$6,500",
    priceNum: 6500,
    image: "/assets/images/usacoins/1936 Oregon Trail Memorial Half Dollar Commem.png",
    description: "Classic commemorative half dollar celebrating the Oregon Trail pioneers. Beautiful design.",
    metal: "Silver",
    category: "usa",
    mint: "US Mint",
    era: "Early 20th Century"
  },

  // ========== MODERN BULLION & PROOFS ==========
  {
    id: 315,
    name: "American Gold Eagle Proof",
    year: "1986",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$4,200",
    priceNum: 4200,
    image: "/assets/images/usacoins/1986 $50 Gold American Eagle Proof 70.png",
    description: "First year of issue for the American Gold Eagle. Perfect Proof 70 condition with original mint packaging.",
    metal: "Gold",
    category: "usa",
    mint: "US Mint",
    era: "Modern"
  },
 
  {
    id: 317,
    name: "American Platinum Eagle",
    year: "2023",
    grade: "MS70",
    gradeNum: 70,
    price: "$3,800",
    priceNum: 3800,
    image: "/assets/images/usacoins/The American Platinum Eagles.png",
    description: "Stunning platinum eagle with elegant design and brilliant finish. NGC MS-70 perfect grade.",
    metal: "Platinum",
    category: "usa",
    mint: "US Mint",
    era: "Modern"
  },

 

 
];

const USAPage = () => {
  const theme = useTheme();
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const handlePriceInquiry = (coin) => {
    setSelectedCoin(coin);
    setFormOpen(true);
  };

  // Get premium/diamond coins
  const premiumCoins = usaCoins.filter(c => c.isPremium === true);
  const regularCoins = usaCoins.filter(c => !c.isPremium);

  // Calculate stats
  const goldCount = usaCoins.filter(c => c.metal === 'Gold' || c.metal?.includes('Gold')).length;
  const silverCount = usaCoins.filter(c => c.metal === 'Silver' || c.metal?.includes('Silver')).length;
  const platinumCount = usaCoins.filter(c => c.metal === 'Platinum').length;
  const diamondCount = usaCoins.filter(c => c.isDiamond === true).length;
  const proofCount = usaCoins.filter(c => c.grade?.includes('Proof') || c.gradeNum >= 69).length;

  const getFilteredCoins = () => {
    if (filter === 'all') {
      return usaCoins;
    }
    if (filter === 'premium') {
      return premiumCoins;
    }
    if (filter === 'gold') {
      return usaCoins.filter(c => c.metal === 'Gold' || c.metal?.includes('Gold'));
    }
    if (filter === 'silver') {
      return usaCoins.filter(c => c.metal === 'Silver' || c.metal?.includes('Silver'));
    }
    if (filter === 'platinum') {
      return usaCoins.filter(c => c.metal === 'Platinum');
    }
    if (filter === 'goldrush') {
      return usaCoins.filter(c => c.era === 'Gold Rush Era');
    }
    if (filter === 'classic') {
      return usaCoins.filter(c => c.era === 'Early 20th Century' || c.era === 'Late 19th Century' || c.era === 'Mid 20th Century');
    }
    if (filter === 'modern') {
      return usaCoins.filter(c => c.era === 'Modern');
    }
    return usaCoins;
  };

  const filteredCoins = getFilteredCoins();

  return (
    <Box sx={{ 
      py: 8,
      backgroundColor: '#F5F5F5',
      minHeight: '100vh',
    }}>
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <MuiLink
            component={Link}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </MuiLink>
          <Typography color="primary">United States & Canadian Collection</Typography>
        </Breadcrumbs>

        {/* Header Section with Animation */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <PublicIcon sx={{ color: 'primary.main', fontSize: 40 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'primary.main',
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                North America
              </Typography>
            </Box>
            
            <Typography variant="h2" sx={{ mb: 3, color: '#2D2D2D' }}>
              American Numismatics
              <Box component="span" sx={{ 
                color: 'primary.main', 
                display: 'block',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                mt: 1
              }}>
                Rare & Historic Coins
              </Box>
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                color: '#616161',
                fontSize: '1.1rem',
                lineHeight: 1.8
              }}
            >
              Explore North America's rich numismatic heritage through our curated collection of classic 
              gold, silver, and platinum coins. From California Gold Rush rarities to modern proofs, 
              each piece tells a unique story of American and Canadian history.
            </Typography>
          </motion.div>
        </Box>

        {/* Diamond Banner - Highlighting Premium Coins */}
        {filter === 'all' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Box 
              sx={{ 
                mb: 4, 
                p: 3, 
                background: `linear-gradient(135deg, ${alpha('#C6A75E', 0.1)} 0%, ${alpha('#C6A75E', 0.05)} 100%)`,
                border: '2px solid',
                borderColor: alpha('#C6A75E', 0.3),
                borderRadius: 3,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ position: 'absolute', top: -20, right: -20, fontSize: '100px', opacity: 0.1 }}>
                💎
              </Box>
              <Typography variant="h5" sx={{ color: '#C6A75E', fontWeight: 600, mb: 1 }}>
                💎 Exclusive Diamond & Premium Collection 💎
              </Typography>
              <Typography variant="body1" sx={{ color: '#616161' }}>
                Featuring our most exquisite pieces with genuine diamonds, gold coin jewelry, and exceptional craftsmanship
              </Typography>
            </Box>
          </motion.div>
        )}

        {/* Quick Stats */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Paper sx={{ px: 3, py: 1.5, textAlign: 'center', backgroundColor: alpha('#C6A75E', 0.05) }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>{usaCoins.length}</Typography>
            <Typography variant="body2" color="#616161">Total Coins</Typography>
          </Paper>
          <Paper sx={{ px: 3, py: 1.5, textAlign: 'center', backgroundColor: alpha('#C6A75E', 0.05) }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>{diamondCount}</Typography>
            <Typography variant="body2" color="#616161">💎 Premium Pieces</Typography>
          </Paper>
          <Paper sx={{ px: 3, py: 1.5, textAlign: 'center', backgroundColor: alpha('#C6A75E', 0.05) }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>{goldCount}</Typography>
            <Typography variant="body2" color="#616161">Gold Coins</Typography>
          </Paper>
          <Paper sx={{ px: 3, py: 1.5, textAlign: 'center', backgroundColor: alpha('#C6A75E', 0.05) }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>{silverCount}</Typography>
            <Typography variant="body2" color="#616161">Silver Coins</Typography>
          </Paper>
          <Paper sx={{ px: 3, py: 1.5, textAlign: 'center', backgroundColor: alpha('#C6A75E', 0.05) }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>{proofCount}+</Typography>
            <Typography variant="body2" color="#616161">Proof Coins</Typography>
          </Paper>
        </Box>

        {/* Filter Chips */}
        <Stack 
          direction="row" 
          spacing={2} 
          justifyContent="center" 
          sx={{ mb: 6 }}
          flexWrap="wrap"
          gap={1}
        >
          <Chip
            label={`All Coins (${usaCoins.length})`}
            onClick={() => setFilter('all')}
            color={filter === 'all' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              py: 2,
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`💎 Premium (${diamondCount})`}
            onClick={() => setFilter('premium')}
            color={filter === 'premium' ? 'primary' : 'default'}
            icon={<span>💎</span>}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Gold (${goldCount})`}
            onClick={() => setFilter('gold')}
            color={filter === 'gold' ? 'primary' : 'default'}
            icon={<AttachMoneyIcon />}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Silver (${silverCount})`}
            onClick={() => setFilter('silver')}
            color={filter === 'silver' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Platinum (${platinumCount})`}
            onClick={() => setFilter('platinum')}
            color={filter === 'platinum' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label="Gold Rush Era"
            onClick={() => setFilter('goldrush')}
            color={filter === 'goldrush' ? 'primary' : 'default'}
            icon={<HistoryIcon />}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label="Classic (1800s-1900s)"
            onClick={() => setFilter('classic')}
            color={filter === 'classic' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label="Modern (1980s-Present)"
            onClick={() => setFilter('modern')}
            color={filter === 'modern' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
        </Stack>

        {/* Results Count */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="#616161">
            Showing {filteredCoins.length} {filter === 'premium' ? 'premium' : 'North American'} {filteredCoins.length === 1 ? 'coin' : 'coins'}
          </Typography>
          <Typography variant="body2" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FilterListIcon sx={{ fontSize: 16 }} /> 
            {filter === 'all' ? 'All Coins' : filter === 'premium' ? '💎 Premium Collection' : filter === 'goldrush' ? 'Gold Rush Era' : filter.charAt(0).toUpperCase() + filter.slice(1)}
          </Typography>
        </Box>

        {/* Coins Grid - Premium coins displayed at top when showing all */}
        {filteredCoins.length > 0 ? (
          <Grid 
            container 
            spacing={4} 
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {filter === 'all' ? (
              <>
                {/* Premium/Diamond Coins Section */}
                {premiumCoins.length > 0 && (
                  <>
                    {premiumCoins.map((coin, index) => (
                      <Grid 
                        item 
                        xs={12} 
                        sm={6} 
                        md={4} 
                        key={`premium-${coin.id}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: index * 0.05 }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <Box sx={{ position: 'relative', width: '100%' }}>
                            {/* Diamond Badge */}
                            <Box 
                              sx={{ 
                                position: 'absolute', 
                                top: -10, 
                                right: 20, 
                                zIndex: 10,
                                backgroundColor: '#C6A75E',
                                color: '#000',
                                px: 2,
                                py: 0.5,
                                borderRadius: 20,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                              }}
                            >
                              {coin.isDiamond ? '💎' : '👑'} {coin.isDiamond ? 'Diamond Collection' : 'Premium Piece'}
                            </Box>
                            <CoinCard 
                              coin={coin} 
                              onPriceInquiry={() => handlePriceInquiry(coin)}
                            />
                          </Box>
                        </motion.div>
                      </Grid>
                    ))}
                    
                    {/* Divider between premium and regular coins */}
                    {regularCoins.length > 0 && (
                      <Grid item xs={12}>
                        <Box 
                          sx={{ 
                            my: 4, 
                            textAlign: 'center',
                            position: 'relative'
                          }}
                        >
                          <Divider sx={{ mb: 3 }}>
                            <Chip 
                              label="Classic & Investment Coins" 
                              sx={{ 
                                backgroundColor: alpha('#C6A75E', 0.1),
                                color: '#C6A75E',
                                fontWeight: 600
                              }} 
                            />
                          </Divider>
                        </Box>
                      </Grid>
                    )}
                    
                    {/* Regular Coins */}
                    {regularCoins.map((coin, index) => (
                      <Grid 
                        item 
                        xs={12} 
                        sm={6} 
                        md={4} 
                        key={`regular-${coin.id}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <motion.div
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: (premiumCoins.length + index) * 0.05 }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <CoinCard 
                            coin={coin} 
                            onPriceInquiry={() => handlePriceInquiry(coin)}
                          />
                        </motion.div>
                      </Grid>
                    ))}
                  </>
                )}
              </>
            ) : (
              /* Normal display for filtered views */
              filteredCoins.map((coin, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={6} 
                  md={4} 
                  key={coin.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <CoinCard 
                      coin={coin} 
                      onPriceInquiry={() => handlePriceInquiry(coin)}
                    />
                  </motion.div>
                </Grid>
              ))
            )}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="#616161">
              No coins match the selected filter.
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => setFilter('all')}
              sx={{ mt: 2 }}
            >
              View All Coins
            </Button>
          </Box>
        )}

        {/* Back Button */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Button
            component={Link}
            to="/"
            variant="outlined"
            size="large"
            startIcon={<ArrowBackIcon />}
            sx={{ 
              px: 6, 
              py: 1.5,
              borderColor: alpha('#9E9E9E', 0.3),
              color: '#616161',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
              }
            }}
          >
            Back to Home
          </Button>
        </Box>

        {/* Info Box */}
        <Paper
          sx={{
            mt: 8,
            p: 4,
            backgroundColor: alpha('#C6A75E', 0.05),
            border: '1px solid',
            borderColor: alpha('#C6A75E', 0.2),
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ color: '#2D2D2D', fontWeight: 600, mb: 2 }}>
            About North American Coinage
          </Typography>
          <Typography variant="body1" color="#616161" paragraph sx={{ lineHeight: 1.8 }}>
            From the iconic Saint-Gaudens Double Eagle to the beloved Morgan Silver Dollar, American coins 
            represent a rich tapestry of history, art, and craftsmanship. The United States Mint has 
            produced some of the world's most recognized and collected coins since 1792, while the Royal 
            Canadian Mint is renowned for its innovative purity and security features.
          </Typography>
          <Typography variant="body1" color="#616161" paragraph sx={{ lineHeight: 1.8 }}>
            Our collection spans centuries of North American numismatics, including California Gold Rush era specimens,
            classic Liberty Head designs, rare Carson City silver dollars, Canadian voyageur dollars, and modern proofs. 
            Each coin is certified by PCGS or NGC and comes with full provenance and authenticity guarantees.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
            {['PCGS Certified', 'NGC Certified', 'Historical Provenance', 'Insured Shipping', 'Free Authentication', 'Lifetime Guarantee', 'Diamond Accents Available'].map((item) => (
              <Chip 
                key={item}
                label={item}
                size="small"
                sx={{ 
                  backgroundColor: alpha('#C6A75E', 0.1),
                  color: '#616161',
                  fontWeight: 500
                }}
              />
            ))}
          </Box>
        </Paper>
      </Container>

      {/* Application Form */}
      <ApplicationForm 
        open={formOpen}
        onClose={() => setFormOpen(false)}
        coin={selectedCoin}
      />
    </Box>
  );
};

export { usaCoins };
export default USAPage;